import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import LoadingSpinner from "../common/LoadingSpinner";
import ModuleUsageCard from "./components/ModuleUsageCard";
import AdminUsersTable from "./components/AdminUsersTable";
import {
  formatAdminLabel,
  formatBytes,
  formatLimit,
  gbToBytes,
  toGbValue,
} from "./data/storageUtils";
import "./SuperadminStorage.css";

ChartJS.register(ArcElement, Tooltip, Legend);

const API_URL = process.env.REACT_APP_API_URL;

const SuperadminStorage = () => {
  const [overview, setOverview] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState("");
  const [adminDetails, setAdminDetails] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const [adminPage, setAdminPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [activeTab, setActiveTab] = useState("overview");
  const [moduleModal, setModuleModal] = useState({ open: false, module: "", files: [], loading: false });
  const [planLimitGb, setPlanLimitGb] = useState("");
  const [addonGb, setAddonGb] = useState("");
  const [packageName, setPackageName] = useState("");
  const [note, setNote] = useState("");
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [userLimitEdits, setUserLimitEdits] = useState({});
  const [savingUserId, setSavingUserId] = useState("");
  const [recentSearch, setRecentSearch] = useState("");

  const loadOverview = async () => {
    try {
      setLoadingOverview(true);
      const response = await axios.get(`${API_URL}api/superadmin/storage/overview/`, { withCredentials: true });
      setOverview(response.data);
      setAdmins(response.data?.admins || []);
    } catch (error) {
      toast.error("Failed to load storage overview.");
    } finally {
      setLoadingOverview(false);
    }
  };

  const loadAdminDetails = async (adminId) => {
    if (!adminId) return;
    try {
      setLoadingDetails(true);
      const response = await axios.get(`${API_URL}api/superadmin/storage/admin/${adminId}/`, { withCredentials: true });
      setAdminDetails(response.data);
    } catch (error) {
      toast.error("Failed to load admin storage details.");
    } finally {
      setLoadingDetails(false);
    }
  };

  const loadModuleFiles = async (adminId, module) => {
    if (!adminId || !module) return;
    setModuleModal({ open: true, module, files: [], loading: true });
    try {
      const response = await axios.get(`${API_URL}api/superadmin/storage/admin/${adminId}/module/${module}/`, { withCredentials: true });
      setModuleModal({ open: true, module, files: response.data?.files || [], loading: false });
    } catch (error) {
      toast.error("Failed to load module files.");
      setModuleModal({ open: true, module, files: [], loading: false });
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  useEffect(() => {
    if (selectedAdminId) {
      loadAdminDetails(selectedAdminId);
    }
  }, [selectedAdminId]);

  useEffect(() => {
    if (adminDetails?.admin) {
      setPlanLimitGb(toGbValue(adminDetails.admin.storage_limit_bytes));
      setAddonGb(toGbValue(adminDetails.admin.addon_storage_bytes));
      setPackageName(adminDetails.admin.package_name || "");
      setNote("");
      setUserLimitEdits({});
    }
  }, [adminDetails]);

  const filteredAdmins = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    let rows = admins;
    if (filterMode === "bounded") {
      rows = rows.filter((admin) => (admin.storage_limit_bytes || 0) > 0);
    }
    if (filterMode === "unlimited") {
      rows = rows.filter((admin) => !(admin.storage_limit_bytes || 0));
    }
    if (filterMode === "high") {
      rows = rows.filter((admin) => (admin.utilization_pct || 0) >= 80);
    }
    if (!term) return rows;
    return rows.filter((admin) => {
      const haystack = `${admin.company_name || ""} ${admin.admin_name || ""} ${admin.admin_id || ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [admins, searchTerm, filterMode]);

  const pagedAdmins = useMemo(() => {
    const start = (adminPage - 1) * pageSize;
    return filteredAdmins.slice(start, start + pageSize);
  }, [filteredAdmins, adminPage, pageSize]);

  const companyShareChart = useMemo(() => {
    const sorted = [...admins].sort((a, b) => (b.storage_used_bytes || 0) - (a.storage_used_bytes || 0));
    const top = sorted.slice(0, 6);
    const others = sorted.slice(6);
    const topTotal = top.reduce((sum, row) => sum + (row.storage_used_bytes || 0), 0);
    const otherTotal = others.reduce((sum, row) => sum + (row.storage_used_bytes || 0), 0);
    const labels = top.map((a) => a.company_name || a.admin_name || a.admin_id);
    const data = top.map((a) => a.storage_used_bytes || 0);
    if (otherTotal > 0) {
      labels.push("Others");
      data.push(otherTotal);
    }
    return {
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: ["#003049", "#2a9d8f", "#e9c46a", "#f4a261", "#e76f51", "#264653", "#adb5bd"],
            borderWidth: 0,
          },
        ],
      },
    };
  }, [admins]);

  const doughnutOptions = useMemo(() => ({
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: { boxWidth: 10, boxHeight: 10 },
      },
    },
  }), []);

  const companyUsageRows = useMemo(() => {
    const sorted = [...admins].sort((a, b) => (b.storage_used_bytes || 0) - (a.storage_used_bytes || 0));
    const totalUsed = sorted.reduce((sum, row) => sum + (row.storage_used_bytes || 0), 0);
    return sorted.slice(0, 8).map((row) => ({
      label: row.company_name || row.admin_name || row.admin_id,
      usedBytes: row.storage_used_bytes || 0,
      percent: totalUsed ? Math.round(((row.storage_used_bytes || 0) / totalUsed) * 100) : 0,
    }));
  }, [admins]);

  const adminChart = useMemo(() => {
    const used = adminDetails?.admin?.storage_used_bytes || 0;
    const allowed = adminDetails?.admin?.total_allowed_bytes || 0;
    const remaining = Math.max(allowed - used, 0);
    const hasBounded = allowed > 0;
    return {
      data: {
        labels: hasBounded ? ["Used", "Remaining"] : ["Used"],
        datasets: [
          {
            data: hasBounded ? [used, remaining] : [used],
            backgroundColor: hasBounded ? ["#3a86ff", "#caf0f8"] : ["#3a86ff"],
            borderWidth: 0,
          },
        ],
      },
      hasBounded,
    };
  }, [adminDetails]);
  const modules = adminDetails?.modules || [];


  const handleAdminSave = async () => {
    if (!adminDetails?.admin?.admin_id) return;
    try {
      setSavingAdmin(true);
      const payload = {
        storage_limit_bytes: gbToBytes(planLimitGb),
        addon_storage_bytes: gbToBytes(addonGb),
        package_name: packageName,
        note: note || "",
      };
      await axios.post(`${API_URL}api/superadmin/storage/admin/${adminDetails.admin.admin_id}/update/`, payload, { withCredentials: true });
      toast.success("Storage limits updated.");
      await Promise.all([loadOverview(), loadAdminDetails(adminDetails.admin.admin_id)]);
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to update storage limits.";
      toast.error(message);
    } finally {
      setSavingAdmin(false);
    }
  };

  const adjustAddon = (delta) => {
    const current = Number(addonGb || 0);
    const next = Math.max(0, Math.round((current + delta) * 100) / 100);
    setAddonGb(next);
  };

  const filteredRecentFiles = useMemo(() => {
    const files = adminDetails?.recent_files || [];
    const term = recentSearch.trim().toLowerCase();
    if (!term) return files;
    return files.filter((file) => {
      const haystack = `${file.original_filename || ""} ${file.module || ""} ${file.user_id || ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [adminDetails, recentSearch]);

  const updateUserLimit = async (userId) => {
    if (!adminDetails?.admin?.admin_id) return;
    try {
      setSavingUserId(userId);
      const value = userLimitEdits[userId];
      const limitBytes = gbToBytes(value || 0);
      await axios.post(
        `${API_URL}api/superadmin/storage/user/${adminDetails.admin.admin_id}/${userId}/update/`,
        { storage_limit_bytes: limitBytes },
        { withCredentials: true }
      );
      toast.success("User storage limit updated.");
      await loadAdminDetails(adminDetails.admin.admin_id);
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to update user limit.";
      toast.error(message);
    } finally {
      setSavingUserId("");
    }
  };

  if (loadingOverview) {
    return <LoadingSpinner message="Loading storage analytics..." />;
  }

  return (
    <div className="container-fluid py-4 sa-page">
          <div className="row mb-4">
            <div className="col-12">
              <div className="card shadow-sm border-0 rounded-4 p-4 text-white sa-card" style={{ background: "linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)" }}>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h2 className="fw-bold mb-1">Storage Management</h2>
                    <p className="mb-0 opacity-75">CARA Admin + User storage analytics and S3 limit controls.</p>
                  </div>
                  <div className="bg-white bg-opacity-10 p-3 rounded-circle">
                    <i className="fas fa-database fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <div className="card border-0 shadow-sm rounded-4 sa-card">
                <div className="card-body p-3 d-flex flex-wrap justify-content-between align-items-center gap-2 sa-tabbar">
                  <div className="d-flex gap-2">
                    <button
                      className={`btn ${activeTab === "overview" ? "btn-primary" : "btn-outline-primary"} px-4`}
                      onClick={() => setActiveTab("overview")}
                    >
                      Overview
                    </button>
                    <button
                      className={`btn ${activeTab === "admin" ? "btn-primary" : "btn-outline-primary"} px-4`}
                      onClick={() => setActiveTab("admin")}
                    >
                      Admin Details
                    </button>
                  </div>
                  <div className="text-muted small">
                    {activeTab === "overview"
                      ? "Company-wide analytics and CARA Admin storage table."
                      : "Selected CARA Admin controls, user limits, and module usage."}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {activeTab === "overview" && (
          <>
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <div className="card h-100 border-0 shadow-sm rounded-4 sa-card">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="bg-primary bg-opacity-10 p-2 rounded-3 text-primary me-3">
                      <i className="fas fa-user-tie"></i>
                    </div>
                    <h6 className="mb-0">Active CARA Admins</h6>
                  </div>
                  <h3 className="fw-bold mb-0">{overview?.totals?.admin_count ?? 0}</h3>
                  <small className="text-muted">Tracked storage accounts</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card h-100 border-0 shadow-sm rounded-4 sa-card">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="bg-success bg-opacity-10 p-2 rounded-3 text-success me-3">
                      <i className="fas fa-users"></i>
                    </div>
                    <h6 className="mb-0">CARA Users</h6>
                  </div>
                  <h3 className="fw-bold mb-0">{overview?.totals?.user_count ?? 0}</h3>
                  <small className="text-muted">Users with usage data</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card h-100 border-0 shadow-sm rounded-4 sa-card">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="bg-warning bg-opacity-10 p-2 rounded-3 text-warning me-3">
                      <i className="fas fa-hdd"></i>
                    </div>
                    <h6 className="mb-0">Total Used</h6>
                  </div>
                  <h3 className="fw-bold mb-0">{formatBytes(overview?.totals?.total_used_bytes)}</h3>
                  <small className="text-muted">Across all organizations</small>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="card h-100 border-0 shadow-sm rounded-4 sa-card">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-2">
                    <div className="bg-info bg-opacity-10 p-2 rounded-3 text-info me-3">
                      <i className="fas fa-chart-line"></i>
                    </div>
                    <h6 className="mb-0">Bounded Utilization</h6>
                  </div>
                  <h3 className="fw-bold mb-0">
                    {overview?.totals?.total_allowed_bytes
                      ? `${Math.round((overview.totals.total_used_bytes / overview.totals.total_allowed_bytes) * 100)}%`
                      : "—"}
                  </h3>
                  <small className="text-muted">Only admins with limits</small>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm rounded-4 h-100 sa-card">
                <div className="card-header bg-white border-0 py-3 rounded-top-4">
                  <h6 className="mb-0 fw-bold">Platform Storage Snapshot</h6>
                  <small className="text-muted">All CARA Admins (used + allowed)</small>
                </div>
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small">Total Used</span>
                    <span className="fw-semibold">{formatBytes(overview?.totals?.total_used_bytes)}</span>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small">Total Allowed</span>
                    <span className="fw-semibold">
                      {overview?.totals?.total_allowed_bytes ? formatBytes(overview?.totals?.total_allowed_bytes) : "Unlimited"}
                    </span>
                  </div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <span className="text-muted small">Unlimited Admins</span>
                    <span className="fw-semibold">{overview?.totals?.total_unlimited_admins ?? 0}</span>
                  </div>
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="text-muted small">Total Files</span>
                    <span className="fw-semibold">{overview?.totals?.total_files ?? 0}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm rounded-4 h-100 sa-card">
                <div className="card-header bg-white border-0 py-3 rounded-top-4">
                  <h6 className="mb-0 fw-bold">Company-wise Usage Share</h6>
                  <small className="text-muted">Top companies by storage consumption</small>
                </div>
                <div className="card-body">
                  <div className="row g-3 align-items-center">
                    <div className="col-md-5">
                      <div className="sa-chart">
                        <Doughnut data={companyShareChart.data} options={doughnutOptions} />
                      </div>
                    </div>
                    <div className="col-md-7">
                      <div className="sa-usage-list">
                        {companyUsageRows.map((row) => (
                          <div key={row.label} className="sa-usage-item">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <span className="fw-semibold">{row.label}</span>
                              <span className="text-muted small">{formatBytes(row.usedBytes)}</span>
                            </div>
                            <div className="sa-usage-bar mb-1">
                              <span style={{ width: `${row.percent}%` }}></span>
                            </div>
                            <div className="text-muted small">{row.percent}% of total usage</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12">
              <div className="card border-0 shadow-sm rounded-4 h-100 sa-card">
                <div className="card-header bg-white border-0 py-3 rounded-top-4">
                  <div className="d-flex align-items-center justify-content-between">
                    <h5 className="mb-0 fw-bold">CARA Admin Storage</h5>
                    <div className="d-flex gap-2">
                      <select
                        className="form-select form-select-sm"
                        value={filterMode}
                        onChange={(e) => {
                          setFilterMode(e.target.value);
                          setAdminPage(1);
                        }}
                      >
                        <option value="all">All</option>
                        <option value="bounded">Bounded Plans</option>
                        <option value="unlimited">Unlimited</option>
                        <option value="high">80%+ Used</option>
                      </select>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search admin or company..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setAdminPage(1);
                        }}
                        style={{ maxWidth: "210px" }}
                      />
                    </div>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive sa-table-scroll">
                    <table className="table table-hover table-lg mb-0 sa-table-compact">
                      <thead className="table-light">
                        <tr>
                          <th>Sr</th>
                          <th>Organization</th>
                          <th>Plan + Add-on</th>
                          <th>Used</th>
                          <th>Utilization</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedAdmins.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center text-muted py-4">
                              No storage records found.
                            </td>
                          </tr>
                        )}
                        {pagedAdmins.map((admin, idx) => {
                          const isSelected = admin.admin_id === selectedAdminId;
                          const totalAllowed = admin.total_allowed_bytes ? formatBytes(admin.total_allowed_bytes) : "Unlimited";
                          return (
                            <tr key={admin.admin_id} className={isSelected ? "table-active" : ""}>
                              <td className="text-muted">{(adminPage - 1) * pageSize + idx + 1}</td>
                              <td>
                                <div className="fw-semibold">{formatAdminLabel(admin)}</div>
                                <div className="text-muted small">{admin.email || admin.admin_id}</div>
                              </td>
                              <td>
                                <div className="small">Plan: {formatLimit(admin.storage_limit_bytes)}</div>
                                <div className="small">Add-on: {formatBytes(admin.addon_storage_bytes)}</div>
                                <div className="text-muted small">Total: {totalAllowed}</div>
                              </td>
                              <td className="fw-semibold">{formatBytes(admin.storage_used_bytes)}</td>
                              <td>
                                {admin.utilization_pct ? (
                                  <>
                                    <div className="progress" style={{ height: "6px" }}>
                                      <div className="progress-bar bg-success" style={{ width: `${Math.min(admin.utilization_pct, 100)}%` }}></div>
                                    </div>
                                    <small className="text-muted">{admin.utilization_pct}%</small>
                                  </>
                                ) : (
                                  <span className="text-muted">Unlimited</span>
                                )}
                              </td>
                              <td className="text-end">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => {
                                    setSelectedAdminId(admin.admin_id);
                                    setActiveTab("admin");
                                  }}
                                >
                                  Manage
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="card-footer bg-white border-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div className="text-muted small">
                    Total files: {overview?.totals?.total_files ?? 0} • Unlimited admins: {overview?.totals?.total_unlimited_admins ?? 0}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setAdminPage((p) => Math.max(p - 1, 1))}
                      disabled={adminPage === 1}
                    >
                      Prev
                    </button>
                    <span className="small text-muted">
                      Page {adminPage} / {Math.max(1, Math.ceil(filteredAdmins.length / pageSize))}
                    </span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setAdminPage((p) => Math.min(p + 1, Math.ceil(filteredAdmins.length / pageSize)))}
                      disabled={adminPage >= Math.ceil(filteredAdmins.length / pageSize)}
                    >
                      Next
                    </button>
                    <select
                      className="form-select form-select-sm"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setAdminPage(1);
                      }}
                    >
                      <option value={6}>6 rows</option>
                      <option value={8}>8 rows</option>
                      <option value={12}>12 rows</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

          </div>

          </>
          )}

          {activeTab === "admin" && (
            <div className="row g-3 mt-1">
              <div className="col-lg-5">
                <div className="card border-0 shadow-sm rounded-4 h-100 sa-card">
                  <div className="card-header bg-white border-0 py-3 rounded-top-4">
                    <h5 className="mb-0 fw-bold">Admin Control Panel</h5>
                    <p className="text-muted small mb-0">Select a CARA Admin to review limits, usage, and user-level analytics.</p>
                  </div>
                  <div className="card-body">
                    {loadingDetails && <LoadingSpinner message="Loading admin details..." />}
                    {!loadingDetails && !adminDetails?.admin && (
                      <div className="text-muted">Pick an admin from the list to view detailed analytics.</div>
                    )}
                    {!loadingDetails && adminDetails?.admin && (
                      <>
                        <div className="mb-3">
                          <h6 className="fw-bold mb-1">{formatAdminLabel(adminDetails.admin)}</h6>
                          <div className="text-muted small">{adminDetails.admin.email || adminDetails.admin.admin_id}</div>
                        </div>

                        <div className="d-flex justify-content-center mb-3">
                          <div style={{ width: "200px" }}>
                            <Doughnut data={adminChart.data} options={doughnutOptions} />
                          </div>
                        </div>

                        <div className="mb-3">
                          <div className="d-flex justify-content-between">
                            <span className="small text-muted">Storage Used</span>
                            <span className="small fw-semibold">{formatBytes(adminDetails.admin.storage_used_bytes)}</span>
                          </div>
                          <div className="d-flex justify-content-between">
                            <span className="small text-muted">Total Allowed</span>
                            <span className="small fw-semibold">
                              {adminDetails.admin.total_allowed_bytes ? formatBytes(adminDetails.admin.total_allowed_bytes) : "Unlimited"}
                            </span>
                          </div>
                          <div className="progress mt-2" style={{ height: "8px" }}>
                            <div
                              className="progress-bar bg-info"
                              style={{ width: `${Math.min(adminDetails.admin.utilization_pct || 0, 100)}%` }}
                            ></div>
                          </div>
                          <small className="text-muted">Utilization: {adminDetails.admin.utilization_pct ? `${adminDetails.admin.utilization_pct}%` : "Unlimited"}</small>
                        </div>

                        <div className="row g-2 mb-3">
                          <div className="col-12">
                            <label className="form-label small text-muted">Package Name</label>
                            <input
                              className="form-control"
                              value={packageName}
                              onChange={(e) => setPackageName(e.target.value)}
                              placeholder="Starter / Pro / Enterprise"
                            />
                          </div>
                          <div className="col-6">
                            <label className="form-label small text-muted">Plan Limit (GB)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              className="form-control"
                              value={planLimitGb}
                              onChange={(e) => setPlanLimitGb(e.target.value)}
                              placeholder="0 = Unlimited"
                            />
                          </div>
                          <div className="col-6">
                            <label className="form-label small text-muted">Add-on (GB)</label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              className="form-control"
                              value={addonGb}
                              onChange={(e) => setAddonGb(e.target.value)}
                              placeholder="0"
                            />
                          </div>
                        </div>

                        <div className="d-flex gap-2 flex-wrap mb-3">
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => adjustAddon(10)}>
                            +10 GB Add-on
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => adjustAddon(50)}>
                            +50 GB Add-on
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => adjustAddon(-10)}>
                            -10 GB Add-on
                          </button>
                        </div>

                        <div className="mb-3">
                          <label className="form-label small text-muted">Change Note (optional)</label>
                          <textarea
                            className="form-control"
                            rows="2"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Add a reason or approval reference for this change."
                          />
                        </div>

                        <button className="btn btn-primary w-100" onClick={handleAdminSave} disabled={savingAdmin}>
                          {savingAdmin ? "Saving..." : "Save Storage Settings"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-lg-7">
                <div className="card border-0 shadow-sm rounded-4 h-100 sa-card">
                  <div className="card-header bg-white border-0 py-3 rounded-top-4">
                    <h5 className="mb-0 fw-bold">CARA User Storage</h5>
                    <p className="text-muted small mb-0">Track usage per CARA user and adjust limits when required.</p>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <AdminUsersTable
                        users={adminDetails?.users || []}
                        userLimitEdits={userLimitEdits}
                        setUserLimitEdits={setUserLimitEdits}
                        savingUserId={savingUserId}
                        updateUserLimit={updateUserLimit}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <ModuleUsageCard
                  modules={modules}
                  adminId={adminDetails?.admin?.admin_id}
                  loadModuleFiles={loadModuleFiles}
                  doughnutOptions={doughnutOptions}
                />

                <div className="card border-0 shadow-sm rounded-4 sa-card">
                  <div className="card-header bg-white border-0 py-3 rounded-top-4">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h5 className="mb-0 fw-bold">Recent Files</h5>
                        <p className="text-muted small mb-0">Latest uploads for quick auditing.</p>
                      </div>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search files..."
                        value={recentSearch}
                        onChange={(e) => setRecentSearch(e.target.value)}
                        style={{ maxWidth: "180px" }}
                      />
                    </div>
                  </div>
                  <div className="card-body">
                    {filteredRecentFiles.length === 0 && (
                      <div className="text-muted">No recent uploads recorded.</div>
                    )}
                    {filteredRecentFiles.length > 0 && (
                      <div className="table-responsive" style={{ maxHeight: "300px", overflowY: "auto" }}>
                        <table className="table table-sm mb-0 sa-table-compact sa-recent-table">
                          <thead className="table-light">
                            <tr>
                              <th>Sr</th>
                              <th>Filename</th>
                              <th>Module</th>
                              {/* <th>User</th> */}
                              <th>Size</th>
                              <th>Uploaded</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRecentFiles.map((file, index) => (
                              <tr key={`${file.s3_key}-${index}`} className="sa-click-row">
                                <td className="text-muted">{index + 1}</td>
                                <td className="fw-semibold">{file.original_filename || file.s3_key}</td>
                                <td>{file.module}</td>
                                {/* <td>{file.user_id}</td> */}
                                <td>{formatBytes(file.file_size_bytes)}</td>
                                <td>{file.created_at ? new Date(file.created_at).toLocaleDateString() : "--"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {moduleModal.open && (
            <div className="modal d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.6)" }}>
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Module Files: {moduleModal.module}</h5>
                    <button type="button" className="btn-close" onClick={() => setModuleModal({ open: false, module: "", files: [], loading: false })}></button>
                  </div>
                  <div className="modal-body">
                    {moduleModal.loading && <div className="text-muted">Loading files...</div>}
                    {!moduleModal.loading && moduleModal.files.length === 0 && (
                      <div className="text-muted">No files uploaded for this module.</div>
                    )}
                    {!moduleModal.loading && moduleModal.files.length > 0 && (
                      <div className="table-responsive" style={{ maxHeight: "360px", overflowY: "auto" }}>
                        <table className="table table-sm sa-table-compact">
                          <thead className="table-light">
                            <tr>
                              <th>Sr</th>
                              <th>Filename</th>
                              <th>Size</th>
                              {/* <th>User</th> */}
                              <th>Uploaded At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {moduleModal.files.map((file, index) => (
                              <tr key={`${file.s3_key}-${index}`}>
                                <td className="text-muted">{index + 1}</td>
                                <td>{file.original_filename || file.s3_key}</td>
                                <td>{formatBytes(file.file_size_bytes)}</td>
                                {/* <td>{file.user_id}</td> */}
                                <td>{file.created_at ? new Date(file.created_at).toLocaleString() : "--"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setModuleModal({ open: false, module: "", files: [], loading: false })}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
  );
};

export default SuperadminStorage;
